import { BookOpen, CheckCircle2, Clock, FileQuestion } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import EmptyState from '../ui/EmptyState';
// Styles live in ../education/CourseLayout.css because the panel's
// responsive behavior (desktop sidebar vs mobile horizontal strip) is driven
// by .course-layout's @media rules — see EducationView which imports it.

export type LessonListItem = {
    id: string;
    title: string;
    duration: string;
    completed: boolean;
    hasQuiz?: boolean;
};

export type LessonsPanelProps = {
    lessons: LessonListItem[];
    activeLessonId: string | null;
    loading: boolean;
    onSelect: (lessonId: string) => void;
};

function LessonsPanel({
    lessons,
    activeLessonId,
    loading,
    onSelect,
}: LessonsPanelProps) {
    return (
        <aside className="lessons-panel" aria-label="Course lessons">
            <h3 className="lessons-panel__title">
                <BookOpen size={18} />
                Course content
            </h3>
            <div className="lessons-panel__list">
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} width="100%" height="52px" borderRadius="10px" />
                    ))
                ) : lessons.length === 0 ? (
                    <EmptyState
                        icon={<BookOpen size={24} />}
                        title="No lessons yet"
                        description="This course hasn't published any lessons."
                    />
                ) : (
                    lessons.map((lesson, idx) => {
                        const isActive = lesson.id === activeLessonId;
                        return (
                            <button
                                key={lesson.id}
                                type="button"
                                className={`lessons-panel__item ${isActive ? 'is-active' : ''} ${lesson.completed ? 'is-completed' : ''}`}
                                onClick={() => onSelect(lesson.id)}
                            >
                                <span className="lessons-panel__number">
                                    {lesson.completed ? (
                                        <CheckCircle2 size={18} />
                                    ) : (
                                        idx + 1
                                    )}
                                </span>
                                <span className="lessons-panel__info">
                                    <span className="lessons-panel__lesson-title">
                                        {lesson.title}
                                    </span>
                                    <span className="lessons-panel__lesson-meta">
                                        <Clock size={12} />
                                        {lesson.duration}
                                        {lesson.hasQuiz && (
                                            <span className="lessons-panel__quiz-badge">
                                                <FileQuestion size={12} />
                                                Quiz
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </button>
                        );
                    })
                )}
            </div>
        </aside>
    );
}

export default LessonsPanel;
